import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import useAppSelector from 'hooks/useAppSelector';
import { ShopProductStockSku, StockLevel } from 'microshop-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollectionVariation, getSkuStock, StockOptions } from 'store/reducers/productSlice';
import styled from 'styled-components';
import { QuantityChangedFunction } from './ProductBuy';

type ArticleSelectorProps = {
    className?: string;
    variation: CollectionVariation;
    onQuantityChange: QuantityChangedFunction;
    cells: Map<string, number | undefined>;
    addToCartEnabled: boolean;
    variationMinQts: {
        [key: string]: number;
    };
    stockSettings?: StockOptions;
};
const ArticleSelector: React.FC<ArticleSelectorProps> = ({
    className,
    variation,
    onQuantityChange,
    cells,
    addToCartEnabled,
    variationMinQts,
    stockSettings,
}) => {
    const { t } = useTranslation();
    const [selectedSku, setSelectedSku] = useState<string>();
    const displayStock = useAppSelector(({ shop }) => shop).shop?.useProductStock;
    const skus = variation.skus;

    const small = !!skus && skus.length <= 1;

    const stock = {
        minQuantity: variation.minQuantity,
        leftToMinQty: variation.variationNumber
            ? variationMinQts[variation.variationNumber] ?? variation.minQuantity
            : 0,
    };

    return (
        <>
            <ArticleSelectContainer small={small} className={`my-4 ${className ? className : ''}`}>
                <Flex justify="between">
                    <MatrixContainer small={small} className="w-100">
                        {skus?.map((sku, i) => {
                            const skuStock = stockSettings && getSkuStock(stockSettings.stockInfo, sku.stock ?? []);
                            return (
                                <ArticleSelect
                                    selected={sku.sku === selectedSku}
                                    key={`${sku.sku}${i}`}
                                    sku={sku}
                                    onSelect={() => {
                                        if (!sku.sku || !variation.variationNumber) return;

                                        onQuantityChange(sku.sku, 1, variation, true);
                                        setSelectedSku(sku.sku);
                                    }}
                                    singleSize={false}
                                    leftToMinQty={displayStock ? stock.leftToMinQty : undefined}
                                    minQuantity={displayStock ? stock.minQuantity : undefined}
                                    stockLevel={displayStock ? skuStock?.stockLevel : undefined}
                                />
                            );
                        })}
                    </MatrixContainer>
                </Flex>
            </ArticleSelectContainer>
        </>
    );
};

type ArticleSelectProps = {
    sku?: ShopProductStockSku;
    onSelect: () => void;
    singleSize: boolean;
    disable?: boolean;
    selected: boolean;

    leftToMinQty?: number;
    minQuantity?: number;
    stockLevel?: StockLevel;
};
export const ArticleSelect = ({
    sku,
    onSelect,
    singleSize,
    selected,
    disable,
    leftToMinQty,
    minQuantity,
    stockLevel,
}: ArticleSelectProps) => {
    const { t } = useTranslation();
    const disabled = disable || stockLevel === StockLevel.OutOfStock;
    const single = singleSize || !sku?.name;

    return (
        <Flex column className="">
            <SizeWrapper align="center" justify="between">
                <OrderSelect
                    disabled={disabled}
                    selected={selected}
                    className="flex-shrink-0 orderselectas"
                    onClick={onSelect}
                />
                {!single && (
                    <>
                        <SizeText disabled={disabled} className="f1-500 text-uppercase">
                            {sku.name}
                        </SizeText>
                    </>
                )}
            </SizeWrapper>
            {leftToMinQty && leftToMinQty > 0 && leftToMinQty !== minQuantity ? (
                <Invalid className="mt-1">{`${t(
                    'cart.minOrderQty',
                    'Minimum order quantity',
                )}: ${minQuantity} (-${leftToMinQty})`}</Invalid>
            ) : null}
        </Flex>
    );
};

const Invalid = styled.div`
    font-size: 14px;
    color: ${({ theme }) => theme.colors.error};
`;

const OrderSelect = styled.button<{ selected: boolean }>`
    height: 100%;
    width: calc(100% - 68px);
    outline: none;
    font-size: 16px;
    flex-grow: 2;
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

    border: ${({ theme }) => `1px solid ${theme.colors.border};`};
    background: ${({ theme }) => theme.colors.transparent};
    border-radius: 1px;

    &:hover,
    &:focus {
        ${({ selected, disabled }) => !selected && !disabled && `border: 1px solid;`}
    }

    ${({ selected, theme }) => selected && `border:3px solid ${theme.colors.accent}`}
`;

const ArticleSelectContainer = styled.div<{ small: boolean }>`
    max-width: ${({ small }) => (small ? '410px' : 'auto')};
`;
const MatrixContainer = styled.div<{ small: boolean }>`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(135px, 1fr));
    gap: 4px;
    max-width: ${({ small }) => (small ? '245px' : 'auto')};
`;

const SizeWrapper = styled(Flex)`
    border-radius: 0px;
    position: relative;
    height: 50px;
`;

type SizeProps = {
    disabled?: boolean;
    small?: boolean;
};
const SizeText = styled(Text)<SizeProps>`
    color: ${({ theme, disabled }) => (disabled ? theme.colors.textDisabled : theme.colors.text)};
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${({ small }: SizeProps) => (small ? 0.875 : 1)}rem;
    white-space: nowrap;
    pointer-events: none;
`;

export default ArticleSelector;
